import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { FormField } from 'ui/formField';
import { NegativeFill } from 'ui/icons';
import { NewTabLink } from 'ui/links';
import { colors, spacing } from 'ui/theme';

import { selectIsEmbeddedMessagingCoreFeatureFlagOn } from 'core/userContext';
import { EmbeddedAction, EmbeddedContentDefinition } from 'modules/graphql';
import { FieldNames, getIsEditMode } from 'modules/templateEditor/common';
import { useEditableFieldsContext } from 'modules/templateEditor/context/EditableFieldsContext';
import { useQueryParamContext } from 'modules/templateEditor/context/QueryParamContext';
import { useValidateContentField } from 'modules/templateEditor/hooks/useValidateContentField';

import { DISPLAY_CONTENT, EmbeddedOpenAction } from '../constants';
import { ActionOption } from '../types';
import { ReadOnlyField, getOpenActionType } from '../utils';
import { RadioWithInputText } from './components';

const RadioOptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.base};
`;

const {
  formOpenActionLabel,
  openActionCustomHelpText,
  openActionTypeCustom,
  openActionTypeNone,
  openActionTypeUrl,
  openActionUrlHelpText,
  urlPlaceholder,
} = DISPLAY_CONTENT.settings;
const { NONE, URL, CUSTOM } = EmbeddedOpenAction;

export const OpenAction = () => {
  const isEmbeddedMessagingEnabled = useSelector(selectIsEmbeddedMessagingCoreFeatureFlagOn);
  const { view } = useQueryParamContext();
  const { initialTemplate, isEditMode, liveTemplate, setCurrentMediumField } =
    useEditableFieldsContext();
  const isEditModeVisible = getIsEditMode(view, isEditMode);
  const { elements } = liveTemplate.mediumData as EmbeddedContentDefinition;
  const defaultAction = elements?.defaultAction;
  const selectedActionType = getOpenActionType(defaultAction);
  const defaultActionData =
    selectedActionType === EmbeddedOpenAction.CUSTOM ? defaultAction?.type : defaultAction?.data;

  const { hasError: hasFieldError, errorText: fieldErrorText } = useValidateContentField(
    FieldNames.DEFAULT_ACTION,
  );

  const initialAction = useMemo(
    () => (initialTemplate.mediumData as EmbeddedContentDefinition)?.elements?.defaultAction,
    [initialTemplate],
  );

  const setDefaultAction = (updatedAction: EmbeddedAction | undefined) =>
    setCurrentMediumField('elements', {
      ...elements,
      defaultAction: updatedAction,
    });

  const handleUrlChange = (url: string) => {
    setDefaultAction({ type: URL, data: url });
  };

  const handleCustomActionChange = (inputValue: string) => {
    setDefaultAction({ type: inputValue, data: '' });
  };

  const handleRadioClick = (openActionType: EmbeddedOpenAction) => {
    const didSelectInitialType = getOpenActionType(initialAction) === openActionType;

    if (openActionType === NONE) return setDefaultAction(undefined);

    const action = didSelectInitialType
      ? initialAction
      : {
          type: openActionType === URL ? openActionType : '',
          data: '',
        };
    setDefaultAction(action);
  };

  const getOpenActionDisplayLabel = (openActionType: EmbeddedOpenAction | string) => {
    switch (openActionType) {
      case URL:
        return openActionTypeUrl;
      case CUSTOM:
        return openActionTypeCustom;
      default:
        return openActionTypeNone;
    }
  };

  const openActionOptions: ActionOption[] = [
    { id: NONE },
    {
      errorText: fieldErrorText,
      hasError: hasFieldError,
      id: URL,
      inputText: {
        helpText: openActionUrlHelpText,
        isDisplayed: selectedActionType === URL,
        onChange: handleUrlChange,
        placeholder: urlPlaceholder,
        value: defaultAction?.data || '',
      },
    },
    /** Custom actions support in SDK will be released alongside placements */
    ...(isEmbeddedMessagingEnabled
      ? [
          {
            id: CUSTOM,
            errorText: fieldErrorText,
            hasError: hasFieldError,
            inputText: {
              helpText: openActionCustomHelpText,
              isDisplayed: selectedActionType === CUSTOM,
              onChange: handleCustomActionChange,
              placeholder: urlPlaceholder,
              value: defaultAction?.type || '',
            },
          },
        ]
      : []),
  ];

  return (
    <FormField
      data-test="embedded-template-settings-form-open-action"
      hasError={isEditModeVisible && hasFieldError}
      labelText={formOpenActionLabel}
      isHorizontalLayout
    >
      {isEditModeVisible ? (
        <RadioOptionsContainer>
          {openActionOptions.map(({ errorText, hasError, id, inputText }) => (
            <RadioWithInputText
              key={`radio-option-open-action-${id}`}
              id={`open-action-${id}`}
              errorText={errorText}
              hasError={hasError}
              inputText={inputText}
              label={getOpenActionDisplayLabel(id as EmbeddedOpenAction)}
              onClick={() => handleRadioClick(id as EmbeddedOpenAction)}
              optionId={id}
              selectedOption={selectedActionType}
              value={defaultAction}
            />
          ))}
        </RadioOptionsContainer>
      ) : (
        <>
          <ReadOnlyField>
            {hasFieldError && <NegativeFill color={colors.negative100} />}
            {getOpenActionDisplayLabel(selectedActionType)}
          </ReadOnlyField>
          {defaultActionData && (
            <NewTabLink
              href={defaultActionData}
              target="_blank"
              rel="noreferrer"
              data-test="default-action-read-only-link-embedded"
            >
              {defaultActionData}
            </NewTabLink>
          )}
        </>
      )}
    </FormField>
  );
};
